
import { useState, useEffect } from 'react';
import { Referral } from '@/components/ReferralList';
import { getCurrentUser } from '@/utils/auth';
import { useReportData } from './useReportData';
import { useToast } from '@/components/ui/use-toast';

export const useDashboardContent = () => {
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [points, setPoints] = useState(0);
  const [referralCode, setReferralCode] = useState('');
  const [referralLink, setReferralLink] = useState('');
  const [user, setUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const { data, chartData } = useReportData();
  const { toast } = useToast();
  
  // Additional properties needed by DashboardContent
  const [lastMonthEarnings, setLastMonthEarnings] = useState(0);
  const [lifetimeEarnings, setLifetimeEarnings] = useState(0);
  const [signupDate, setSignupDate] = useState('');
  const [currentMonth, setCurrentMonth] = useState('');
  const [currentYear, setCurrentYear] = useState('');
  
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Get the current user
        const currentUser = getCurrentUser();
        setUser(currentUser);
        
        // Mock referral data
        const mockReferrals: Referral[] = [
          {
            id: '1',
            name: 'John Doe',
            email: 'john.doe@example.com',
            status: 'pending',
            date: '2023-04-15',
          },
          {
            id: '2',
            name: 'Jane Smith',
            email: 'jane.smith@example.com',
            status: 'approved',
            date: '2023-04-14',
          },
          {
            id: '3',
            name: 'Bob Johnson',
            email: 'bob.johnson@example.com',
            status: 'rejected',
            date: '2023-04-13',
          },
        ];
        setReferrals(mockReferrals);
        
        // Set mock points
        setPoints(1250);
        
        // Set mock referral code and link
        setReferralCode('REF123456');
        setReferralLink('https://example.com/ref/REF123456');
        
        // Set additional mock data
        setLastMonthEarnings(650);
        setLifetimeEarnings(2400);
        setSignupDate('2023-01-15');
        
        // Set current month and year
        const date = new Date();
        setCurrentMonth(date.toLocaleString('default', { month: 'long' }));
        setCurrentYear(date.getFullYear().toString());
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const handleSendReferral = async (data: any, method: string): Promise<void> => {
    // Mock sending a referral
    toast({
      title: 'Referral Sent',
      description: `Referral sent to ${data.email} via ${method}`,
    });
  };
  
  const exportReport = (format: 'csv' | 'pdf') => {
    // Mock export report
    toast({
      title: 'Report Exported',
      description: `Report exported in ${format.toUpperCase()} format`,
    });
  };
  
  return {
    user,
    referrals,
    points,
    referralCode,
    referralLink,
    data,
    chartData,
    isLoading,
    handleSendReferral,
    // Additional properties
    reportData: data,
    lastMonthEarnings,
    lifetimeEarnings,
    signupDate,
    currentMonth,
    currentYear,
    exportReport
  };
};
